import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  CreateFundingBatchRequest,
  ProbeResponse,
  RouteBindingRecord,
  UpstreamCapabilityStatus,
  UpstreamAccountRecord,
  UpstreamCatalogModel,
  UpstreamChannelCostRecord,
  UpstreamConfig,
  UpstreamFundingBatchRecord,
  UpstreamModelCapabilityRecord,
  UpstreamModelMappingRecord,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCommitFundingBatch,
  useCreateFundingBatch,
  useFundingBatches,
  useUpdateFundingBatch,
} from '../api/funding.ts';
import { usePricing } from '../api/pricing.ts';
import { useUpstreamAccountsList } from '../api/useUpstreamAccounts.ts';
import { useModelMappingsList } from '../api/useModelMappings.ts';
import {
  useDeleteUpstream,
  useCreateRouteBinding,
  useDeleteRouteBinding,
  useDiscoverUpstreamCapabilities,
  useIgnoreUpstreamCapability,
  useProbeUpstreamCapability,
  useProbeUpstream,
  useRouteBindings,
  useSetUpstreamChannelCosts,
  useUpdateRouteBinding,
  useUpdateUpstream,
  useUpstreamChannelCosts,
  useUnignoreUpstreamCapability,
  useUpstream,
  useUpstreamCapabilities,
  useUpstreamCatalog,
  useUpstreamRetirementImpact,
} from '../api/useUpstreams.ts';
import { AccountsCard } from '../components/AccountsCard.tsx';
import { Modal } from '../components/Modal.tsx';
import { ProbeHistoryCard } from '../components/ProbeHistoryCard.tsx';
import { ModelMappingsCard } from '../components/ModelMappingsCard.tsx';
import { ProbeResultPanel } from '../components/ProbeResultPanel.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { INPUT_CLASS, INPUT_CLASS_ON_HIGHEST } from '../styles/forms.ts';

type UpstreamTab = 'config' | 'accounts' | 'mappings' | 'funding' | 'probe';

/**
 * `/supply/upstreams/:id` detail view. Three sections:
 *   1. Configuration — endpoint/priority/weight/enabled/secret-ref
 *   2. Model mappings — attached (vendor, channel, model, protocol) tuples
 *   3. Probe — inline probe form + session-scoped result history
 *
 * "Edit mode" is inline-per-field: click the field pencil → blur /
 * Enter to save via PATCH. Stream is in one direction only — there's no
 * optimistic mutation, we wait for the PATCH to return and then let
 * React Query invalidate. Feels sluggish on slow links but stays
 * consistent with the gateway's source of truth.
 *
 * Probe history is in-memory per component instance (cap 10 entries).
 * A history API could land in Phase 2 once gateway persists probe rows
 * beyond `usage_logs`.
 */

const MAX_PROBE_HISTORY = 10;

export function UpstreamDetail(): JSX.Element {
  const t = useT();
  const params = useParams<{ id: string }>();
  const upstreamId = params.id ?? '';
  const upstream = useUpstream(upstreamId);
  const catalog = useUpstreamCatalog();

  const attached = useMemo<UpstreamCatalogModel[]>(() => {
    const entries = catalog.data?.entries ?? [];
    const match = entries.find((e) => e.upstream.id === upstreamId);
    return match?.models ?? [];
  }, [catalog.data, upstreamId]);

  if (upstream.isPending) {
    return (
      <section className="flex flex-col gap-8" data-testid="upstream-detail-loading">
        <div className="h-8 w-64 animate-pulse rounded-lg bg-surface-container" aria-hidden />
        <div className="h-32 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <div className="h-48 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <span className="sr-only">{t('common.loading')}</span>
      </section>
    );
  }

  if (upstream.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-xl bg-error-container p-6 text-on-error-container"
        role="alert"
        data-testid="upstream-detail-error"
      >
        <h2 className="text-base font-semibold">{t('upstream_detail.error.title')}</h2>
        <p className="text-sm">
          {upstream.error instanceof OpsApiError
            ? `${upstream.error.code}: ${upstream.error.message}`
            : t('common.network_error')}
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void upstream.refetch()}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.retry')}
          </button>
          <Link to="/supply/upstreams" className="text-[11px] font-bold uppercase tracking-[0.08em]">
            {t('upstream_detail.back')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <UpstreamDetailReady
      upstream={upstream.data}
      attachedModels={attached}
      catalogLoading={catalog.isPending}
    />
  );
}

interface ReadyProps {
  readonly upstream: UpstreamConfig;
  readonly attachedModels: readonly UpstreamCatalogModel[];
  readonly catalogLoading: boolean;
}

const TAB_IDS: readonly UpstreamTab[] = ['config', 'accounts', 'mappings', 'funding', 'probe'] as const;

const TAB_I18N_KEYS: Record<UpstreamTab, I18nKey> = {
  config: 'upstream_detail.tab.config',
  accounts: 'upstream_detail.tab.accounts',
  mappings: 'upstream_detail.tab.mappings',
  funding: 'upstream_detail.tab.funding',
  probe: 'upstream_detail.tab.probe',
};

function formatDiagnosticSource(no: number): string {
  return `S${Math.max(0, Math.floor(no)).toString().padStart(3, '0')}`;
}

function UpstreamDetailReady({
  upstream,
  attachedModels,
}: ReadyProps): JSX.Element {
  const t = useT();
  const [activeTab, setActiveTab] = useState<UpstreamTab>('config');
  const variant = upstream.enabled ? 'ok' : 'warning';
  const statusLabel = upstream.enabled
    ? t('upstream_detail.chip.enabled')
    : t('upstream_detail.chip.disabled');

  // Summary stats data
  const accountsQ = useUpstreamAccountsList(upstream.id);
  const mappingsQ = useModelMappingsList(upstream.id);
  const fundingQ = useFundingBatches(upstream.id);

  const accountCount = accountsQ.data?.length ?? 0;
  const mappingCount = mappingsQ.data?.length ?? 0;
  const fundingRemainingUsd = useMemo(() => {
    const batches = fundingQ.data;
    if (batches === undefined || batches.length === 0) return null;
    let totalCents = 0;
    for (const b of batches) {
      if (!b.is_depleted) {
        totalCents += b.usd_capacity_cents - b.usd_consumed_cents;
      }
    }
    return totalCents / 100;
  }, [fundingQ.data]);

  return (
    <section className="flex flex-col gap-8" data-testid="upstream-detail-page">
      {/* Breadcrumb + Header */}
      <div>
        <nav aria-label="breadcrumb" className="flex items-center gap-2 text-[12px] text-outline mb-2">
          <Link to="/supply/upstreams" className="hover:text-primary">
            {t('upstream_detail.breadcrumb.upstreams')}
          </Link>
          <span aria-hidden>/</span>
          <span className="text-on-surface-variant">{upstream.name}</span>
        </nav>
        <h2
          className="text-[28px] font-semibold leading-tight text-on-surface"
          data-testid="upstream-detail-title"
        >
          {upstream.name}
        </h2>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          <StatusChip
            variant={variant}
            label={statusLabel}
            testId="upstream-detail-status"
          />
          <span className="rounded-full bg-surface-container-highest px-3 py-0.5 text-[10px] font-medium text-on-surface-variant">
            {upstream.kind}
          </span>
          <span>{t('upstream_detail.meta.visibility', { visibility: upstream.tenant_visibility })}</span>
        </div>

        {/* Summary Stats Bar */}
        <div
          className="mt-6 grid grid-cols-5 gap-px overflow-hidden rounded-xl border border-outline-variant/10 bg-surface-container-high p-4"
          data-testid="upstream-summary-stats"
        >
          <div className="flex flex-col px-4">
            <span className="mb-1 text-[11px] font-medium text-outline">
              {t('upstream_detail.summary.source')}
            </span>
            <span className="font-mono text-[18px] font-black tracking-tight text-primary">
              {formatDiagnosticSource(upstream.diagnostic_source_no)}
            </span>
          </div>
          <div className="flex flex-col border-l border-outline-variant/20 px-4">
            <span className="mb-1 text-[11px] font-medium text-outline">
              {t('upstream_detail.summary.endpoint')}
            </span>
            <span className="truncate font-mono text-[13px] text-primary">
              {upstream.endpoint_url}
            </span>
          </div>
          <div className="flex flex-col border-l border-outline-variant/20 px-4">
            <span className="mb-1 text-[11px] font-medium text-outline">
              {t('upstream_detail.summary.accounts')}
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-primary">{accountCount}</span>
              {t('upstream_detail.summary.accounts_unit').length > 0 ? (
                <span className="text-[12px] text-outline">
                  {t('upstream_detail.summary.accounts_unit')}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col border-l border-outline-variant/20 px-4">
            <span className="mb-1 text-[11px] font-medium text-outline">
              {t('upstream_detail.summary.mappings')}
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-primary">{mappingCount}</span>
              {t('upstream_detail.summary.mappings_unit').length > 0 ? (
                <span className="text-[12px] text-outline">
                  {t('upstream_detail.summary.mappings_unit')}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col border-l border-outline-variant/20 px-4">
            <span className="mb-1 text-[11px] font-medium text-outline">
              {t('upstream_detail.summary.funding')}
            </span>
            <div className="flex items-baseline gap-1">
              {fundingRemainingUsd !== null ? (
                <>
                  <span className="text-2xl font-bold text-primary">
                    ${fundingRemainingUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-[12px] text-outline">
                    {t('upstream_detail.summary.funding_remaining')}
                  </span>
                </>
              ) : (
                <span className="text-sm text-outline">
                  {t('upstream_detail.summary.funding_none')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div
        className="flex w-max gap-1 rounded-xl bg-surface-container p-1"
        role="tablist"
        aria-label="upstream detail tabs"
        data-testid="upstream-tab-bar"
      >
        {TAB_IDS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={
              activeTab === tab
                ? 'rounded-lg bg-primary-container px-6 py-2 text-sm font-bold text-on-primary shadow-lg shadow-primary/10'
                : 'px-6 py-2 text-sm font-medium text-outline transition-colors hover:text-on-surface'
            }
            data-testid={`upstream-tab-${tab}`}
          >
            {t(TAB_I18N_KEYS[tab])}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div data-testid={`upstream-tab-content-${activeTab}`}>
        {activeTab === 'config' ? (
          <div className="flex flex-col gap-8">
            <ConfigurationCard upstream={upstream} attached={attachedModels} />
            <ChannelCostsCard upstreamId={upstream.id} />
            <DangerZone upstreamId={upstream.id} upstreamName={upstream.name} />
          </div>
        ) : null}
        {activeTab === 'accounts' ? (
          <AccountsCard upstreamId={upstream.id} />
        ) : null}
        {activeTab === 'mappings' ? (
          <div className="flex flex-col gap-8">
            <CapabilitiesRoutingCard upstreamId={upstream.id} />
            <div className="rounded-xl bg-surface-container p-4 text-sm text-outline">
              {t('upstream_detail.routing.legacy_notice')}
            </div>
            <ModelMappingsCard mode="legacy" upstreamId={upstream.id} />
          </div>
        ) : null}
        {activeTab === 'funding' ? (
          <FundingCard upstreamId={upstream.id} accounts={accountsQ.data ?? []} />
        ) : null}
        {activeTab === 'probe' ? (
          <div className="flex flex-col gap-8">
            <ProbeCard
              upstreamId={upstream.id}
              upstreamName={upstream.name}
              mappings={mappingsQ.data ?? []}
            />
            <ProbeHistoryCard upstreamId={upstream.id} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

const CAPABILITY_STATUS_CLASS: Record<UpstreamCapabilityStatus, string> = {
  passed: 'bg-primary-container text-on-primary',
  needs_probe: 'bg-surface-container-highest text-outline',
  probe_failed: 'bg-error-container text-on-error-container',
  missing_from_list: 'bg-surface-container-highest text-outline',
  mismatch: 'bg-error-container text-on-error-container',
  ignored: 'bg-surface-container-high text-outline',
};

const CAPABILITY_PROBE_POLL_INTERVAL_MS = 2500;
const CAPABILITY_PROBE_POLL_TIMEOUT_MS = 120_000;

function capabilityStatusLabel(
  t: (key: I18nKey, params?: Record<string, string | number>) => string,
  status: UpstreamCapabilityStatus,
): string {
  switch (status) {
    case 'passed':
      return t('upstream_detail.routing.capability.status.passed');
    case 'needs_probe':
      return t('upstream_detail.routing.capability.status.needs_probe');
    case 'probe_failed':
      return t('upstream_detail.routing.capability.status.probe_failed');
    case 'missing_from_list':
      return t('upstream_detail.routing.capability.status.missing_from_list');
    case 'mismatch':
      return t('upstream_detail.routing.capability.status.mismatch');
    case 'ignored':
      return t('upstream_detail.routing.capability.status.ignored');
  }
}

function capabilityStatusHint(
  t: (key: I18nKey, params?: Record<string, string | number>) => string,
  capability: UpstreamModelCapabilityRecord,
): string {
  if (capability.status === 'passed' && capability.last_passed_at !== null) {
    return t('upstream_detail.routing.capability.status.passed.hint', {
      time: capability.last_passed_at,
    });
  }
  if (capability.status === 'ignored') {
    return t('upstream_detail.routing.capability.status.ignored.hint');
  }
  if (capability.error !== null && capability.error.length > 0) {
    return capability.error;
  }
  switch (capability.status) {
    case 'passed':
      return t('upstream_detail.routing.capability.status.passed.ready');
    case 'needs_probe':
      return t('upstream_detail.routing.capability.status.needs_probe.hint');
    case 'probe_failed':
      return t('upstream_detail.routing.capability.status.probe_failed.hint');
    case 'missing_from_list':
      return t('upstream_detail.routing.capability.status.missing_from_list.hint');
    case 'mismatch':
      return t('upstream_detail.routing.capability.status.mismatch.hint');
  }
}

function canBindCapability(capability: UpstreamModelCapabilityRecord): boolean {
  return capability.status === 'passed' && capability.last_passed_at !== null;
}

function isCurrentCapability(capability: UpstreamModelCapabilityRecord): boolean {
  return capability.status !== 'mismatch';
}

type CapabilityGroup = {
  readonly key: string;
  readonly channel_slug: string;
  readonly model: string;
  readonly capabilities: readonly UpstreamModelCapabilityRecord[];
  readonly representative: UpstreamModelCapabilityRecord;
  readonly bindable: UpstreamModelCapabilityRecord | null;
  readonly bound: RouteBindingRecord | null;
};

type CapabilitySummary = {
  readonly total: number;
  readonly bindable: number;
  readonly bound: number;
  readonly failed: number;
  readonly needsProbe: number;
  readonly ignored: number;
};

type CapabilityResultFilter = 'all' | 'bindable' | 'bound' | 'failed' | 'needs_probe' | 'ignored';

function capabilitySortTime(capability: UpstreamModelCapabilityRecord): number {
  const parsed = Date.parse(capability.updated_at || capability.last_probe_at || capability.created_at);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function newestCapability(
  capabilities: readonly UpstreamModelCapabilityRecord[],
): UpstreamModelCapabilityRecord {
  return [...capabilities].sort((a, b) => capabilitySortTime(b) - capabilitySortTime(a))[0]!;
}

function groupCapabilities(
  capabilities: readonly UpstreamModelCapabilityRecord[],
  bindingByCapability: ReadonlyMap<string, RouteBindingRecord>,
  focusedCapabilityIds: ReadonlySet<string>,
): CapabilityGroup[] {
  const grouped = new Map<string, UpstreamModelCapabilityRecord[]>();
  for (const capability of capabilities) {
    const key = [
      capability.channel_slug,
      capability.vendor,
      capability.model,
      capability.protocol,
      capability.upstream_model_name,
    ].join('\u0000');
    const rows = grouped.get(key);
    if (rows === undefined) {
      grouped.set(key, [capability]);
    } else {
      rows.push(capability);
    }
  }

  return [...grouped.entries()]
    .map(([key, rows]) => {
      const boundCapability = rows.find((capability) => bindingByCapability.has(capability.id)) ?? null;
      const focusedCapability = rows.find((capability) => focusedCapabilityIds.has(capability.id)) ?? null;
      const bindable = [...rows]
        .filter(canBindCapability)
        .sort((a, b) => capabilitySortTime(b) - capabilitySortTime(a))[0] ?? null;
      const representative = boundCapability ?? focusedCapability ?? bindable ?? newestCapability(rows);
      return {
        key,
        channel_slug: representative.channel_slug,
        model: representative.model,
        capabilities: rows,
        representative,
        bindable,
        bound: boundCapability === null ? null : bindingByCapability.get(boundCapability.id) ?? null,
      };
    })
    .sort((a, b) => a.model.localeCompare(b.model));
}

function isUnboundBindableGroup(group: CapabilityGroup): boolean {
  return group.bound === null && group.bindable !== null;
}

function matchesCapabilityResultFilter(
  group: CapabilityGroup,
  filter: CapabilityResultFilter,
): boolean {
  switch (filter) {
    case 'all':
      return group.representative.status !== 'ignored';
    case 'bindable':
      return isUnboundBindableGroup(group);
    case 'bound':
      return group.bound !== null;
    case 'failed':
      return group.representative.status === 'probe_failed';
    case 'needs_probe':
      return group.representative.status === 'needs_probe';
    case 'ignored':
      return group.representative.status === 'ignored';
  }
}

function CapabilitiesRoutingCard({ upstreamId }: { readonly upstreamId: string }): JSX.Element {
  const t = useT();
  const capabilities = useUpstreamCapabilities(upstreamId);
  const bindings = useRouteBindings(upstreamId);
  const discover = useDiscoverUpstreamCapabilities(upstreamId);
  const probeCapability = useProbeUpstreamCapability(upstreamId);
  const ignoreCapability = useIgnoreUpstreamCapability(upstreamId);
  const unignoreCapability = useUnignoreUpstreamCapability(upstreamId);
  const createBinding = useCreateRouteBinding(upstreamId);
  const updateBinding = useUpdateRouteBinding(upstreamId);
  const deleteBinding = useDeleteRouteBinding(upstreamId);
  const toast = useToast();
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [resultFilter, setResultFilter] = useState<CapabilityResultFilter>('all');
  const [probingCapabilityId, setProbingCapabilityId] = useState<string | null>(null);
  const [pollingCapabilityIds, setPollingCapabilityIds] = useState<readonly string[]>([]);
  const [focusedCapabilityIds, setFocusedCapabilityIds] = useState<readonly string[]>([]);
  const pollingStartedAtRef = useRef<Map<string, number>>(new Map());

  const bindingByCapability = useMemo(() => {
    const map = new Map<string, RouteBindingRecord>();
    for (const binding of bindings.data ?? []) {
      map.set(binding.capability_id, binding);
    }
    return map;
  }, [bindings.data]);

  const currentCapabilities = useMemo(
    () => (capabilities.data ?? []).filter(isCurrentCapability),
    [capabilities.data],
  );
  const capabilityById = useMemo(() => {
    const map = new Map<string, UpstreamModelCapabilityRecord>();
    for (const capability of capabilities.data ?? []) {
      map.set(capability.id, capability);
    }
    return map;
  }, [capabilities.data]);

  const vendorOptions = useMemo(() => {
    const vendors = new Set<string>();
    for (const capability of currentCapabilities) {
      vendors.add(capability.vendor);
    }
    for (const binding of bindings.data ?? []) {
      const capability = capabilityById.get(binding.capability_id);
      if (capability !== undefined && !isCurrentCapability(capability)) {
        continue;
      }
      vendors.add(binding.vendor);
    }
    return [...vendors].sort((a, b) => a.localeCompare(b));
  }, [bindings.data, capabilityById, currentCapabilities]);
  const activeVendor = vendorOptions.includes(selectedVendor)
    ? selectedVendor
    : vendorOptions[0] ?? '';
  const channelOptions = useMemo(() => {
    const channels = new Set<string>();
    for (const capability of currentCapabilities) {
      if (capability.vendor === activeVendor) {
        channels.add(capability.channel_slug);
      }
    }
    for (const binding of bindings.data ?? []) {
      const capability = capabilityById.get(binding.capability_id);
      if (capability !== undefined && !isCurrentCapability(capability)) {
        continue;
      }
      if (binding.vendor === activeVendor) {
        channels.add(binding.channel);
      }
    }
    return [...channels].sort((a, b) => a.localeCompare(b));
  }, [activeVendor, bindings.data, capabilityById, currentCapabilities]);
  const activeChannel = channelOptions.includes(selectedChannel)
    ? selectedChannel
    : channelOptions[0] ?? '';
  const pollingCapabilityIdSet = useMemo(
    () => new Set(pollingCapabilityIds),
    [pollingCapabilityIds],
  );
  const focusedCapabilityIdSet = useMemo(
    () => new Set(focusedCapabilityIds),
    [focusedCapabilityIds],
  );
  const capabilityGroups = useMemo(
    () => groupCapabilities(currentCapabilities, bindingByCapability, focusedCapabilityIdSet),
    [bindingByCapability, currentCapabilities, focusedCapabilityIdSet],
  );
  const scopedCapabilityGroups = capabilityGroups.filter(
    (group) =>
      (activeChannel === '' || group.channel_slug === activeChannel) &&
      group.representative.vendor === activeVendor,
  );
  const visibleCapabilityGroups = scopedCapabilityGroups.filter(
    (group) => matchesCapabilityResultFilter(group, resultFilter),
  );
  const visibleCapabilitySummary: CapabilitySummary = useMemo(() => ({
    total: scopedCapabilityGroups.filter((group) => group.representative.status !== 'ignored').length,
    bindable: scopedCapabilityGroups.filter(isUnboundBindableGroup).length,
    bound: scopedCapabilityGroups.filter((group) => group.bound !== null).length,
    failed: scopedCapabilityGroups.filter((group) => group.representative.status === 'probe_failed').length,
    needsProbe: scopedCapabilityGroups.filter((group) => group.representative.status === 'needs_probe').length,
    ignored: scopedCapabilityGroups.filter((group) => group.representative.status === 'ignored').length,
  }), [scopedCapabilityGroups]);
  const visibleBindings = useMemo(
    () => (bindings.data ?? []).filter((binding) => {
      const capability = capabilityById.get(binding.capability_id);
      if (capability !== undefined && !isCurrentCapability(capability)) {
        return false;
      }
      return (
        (activeChannel === '' || binding.channel === activeChannel) &&
        binding.vendor === activeVendor
      );
    }),
    [activeChannel, activeVendor, bindings.data, capabilityById],
  );

  useEffect(() => {
    if (pollingCapabilityIds.length === 0) {
      return;
    }

    let cancelled = false;

    async function refreshAndReconcile(): Promise<void> {
      const result = await capabilities.refetch();
      if (cancelled) {
        return;
      }
      const rowsById = new Map((result.data ?? []).map((capability) => [capability.id, capability]));
      const now = Date.now();
      setPollingCapabilityIds((prev) => prev.filter((id) => {
        const row = rowsById.get(id);
        if (row !== undefined && row.status !== 'needs_probe') {
          pollingStartedAtRef.current.delete(id);
          return false;
        }
        const startedAt = pollingStartedAtRef.current.get(id) ?? now;
        if (now - startedAt >= CAPABILITY_PROBE_POLL_TIMEOUT_MS) {
          pollingStartedAtRef.current.delete(id);
          return false;
        }
        return true;
      }));
    }

    void refreshAndReconcile();
    const intervalId = window.setInterval(() => {
      void refreshAndReconcile();
    }, CAPABILITY_PROBE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [capabilities.refetch, pollingCapabilityIds.length]);

  function handleDiscover(): void {
    discover.mutate({}, {
      onSuccess: (rows) => toast.success(t('upstream_detail.routing.toast.models_loaded', { count: rows.length })),
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function handleProbeCapability(capability: UpstreamModelCapabilityRecord): void {
    setProbingCapabilityId(capability.id);
    probeCapability.mutate(capability.id, {
      onSuccess: () => {
        pollingStartedAtRef.current.set(capability.id, Date.now());
        setFocusedCapabilityIds((prev) => (
          prev.includes(capability.id) ? prev : [...prev, capability.id]
        ));
        setPollingCapabilityIds((prev) => (
          prev.includes(capability.id) ? prev : [...prev, capability.id]
        ));
        toast.success(t('upstream_detail.routing.toast.capability_probe_queued'));
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
      onSettled: () => setProbingCapabilityId(null),
    });
  }

  function handleIgnoreCapability(capability: UpstreamModelCapabilityRecord): void {
    ignoreCapability.mutate(capability.id, {
      onSuccess: () => {
        setFocusedCapabilityIds((prev) => prev.filter((id) => id !== capability.id));
        setPollingCapabilityIds((prev) => prev.filter((id) => id !== capability.id));
        pollingStartedAtRef.current.delete(capability.id);
        toast.success(t('upstream_detail.routing.toast.capability_ignored'));
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function handleUnignoreCapability(capability: UpstreamModelCapabilityRecord): void {
    unignoreCapability.mutate(capability.id, {
      onSuccess: (row) => {
        setFocusedCapabilityIds((prev) => (
          prev.includes(row.id) ? prev : [...prev, row.id]
        ));
        toast.success(t('upstream_detail.routing.toast.capability_unignored'));
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function handleBind(capability: UpstreamModelCapabilityRecord): void {
    createBinding.mutate({ capability_id: capability.id, enabled: true, priority: 100 }, {
      onSuccess: () => toast.success('路由绑定已创建'),
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function toggleBinding(binding: RouteBindingRecord): void {
    updateBinding.mutate({ binding_id: binding.id, enabled: !binding.enabled }, {
      onSuccess: () => toast.success(binding.enabled ? '路由绑定已停用' : '路由绑定已启用'),
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  function removeBinding(binding: RouteBindingRecord): void {
    deleteBinding.mutate(binding.id, {
      onSuccess: () => toast.success('路由绑定已删除'),
      onError: (err) => toast.error(coerceErrorMessage(err)),
    });
  }

  const capabilityFilterItems: readonly {
    readonly filter: CapabilityResultFilter;
    readonly label: string;
    readonly tone: 'neutral' | 'primary' | 'error';
  }[] = [
    {
      filter: 'all',
      label: t('upstream_detail.routing.capabilities.summary.total', { count: visibleCapabilitySummary.total }),
      tone: 'neutral',
    },
    {
      filter: 'bindable',
      label: t('upstream_detail.routing.capabilities.summary.bindable', { count: visibleCapabilitySummary.bindable }),
      tone: 'primary',
    },
    {
      filter: 'bound',
      label: t('upstream_detail.routing.capabilities.summary.bound', { count: visibleCapabilitySummary.bound }),
      tone: 'neutral',
    },
    {
      filter: 'failed',
      label: t('upstream_detail.routing.capabilities.summary.failed', { count: visibleCapabilitySummary.failed }),
      tone: 'error',
    },
    {
      filter: 'needs_probe',
      label: t('upstream_detail.routing.capabilities.summary.needs_probe', { count: visibleCapabilitySummary.needsProbe }),
      tone: 'neutral',
    },
    {
      filter: 'ignored',
      label: t('upstream_detail.routing.capabilities.summary.ignored', { count: visibleCapabilitySummary.ignored }),
      tone: 'neutral',
    },
  ];

  return (
    <div className="rounded-xl bg-surface-container p-6" data-testid="capabilities-routing-card">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
            {t('upstream_detail.routing.title')}
          </h3>
          <p className="mt-1 text-xs text-outline">
            {t('upstream_detail.routing.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleDiscover}
            disabled={discover.isPending}
            className="rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary disabled:opacity-40"
          >
            {discover.isPending
              ? t('upstream_detail.routing.discovering')
              : t('upstream_detail.routing.discover')}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <div>
          <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.08em] text-outline">
                {t('upstream_detail.routing.capabilities.title')}
              </h4>
              <p className="mt-1 text-xs text-outline">
                {t('upstream_detail.routing.capabilities.subtitle')}
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-end">
              <label className="flex w-full flex-col gap-1 text-xs text-outline sm:w-44">
                <span>{t('upstream_detail.routing.capabilities.filter.vendor')}</span>
                <select
                  value={activeVendor}
                  onChange={(e) => {
                    setSelectedVendor(e.currentTarget.value);
                    setSelectedChannel('');
                  }}
                  disabled={vendorOptions.length === 0}
                  className="rounded-lg bg-surface-container-lowest px-3 py-2 text-xs text-on-surface disabled:opacity-50"
                  data-testid="upstream-capability-vendor"
                >
                  {vendorOptions.length === 0 ? (
                    <option value="">{t('upstream_detail.routing.capabilities.filter.vendor.empty')}</option>
                  ) : (
                    vendorOptions.map((vendor) => (
                      <option key={vendor} value={vendor}>
                        {vendor}
                      </option>
                    ))
                  )}
                </select>
              </label>
              <label className="flex w-full flex-col gap-1 text-xs text-outline sm:w-64">
                <span>{t('upstream_detail.routing.capabilities.filter.channel')}</span>
                <select
                  value={activeChannel}
                  onChange={(e) => setSelectedChannel(e.currentTarget.value)}
                  disabled={activeVendor === '' || channelOptions.length === 0}
                  className="rounded-lg bg-surface-container-lowest px-3 py-2 text-xs text-on-surface disabled:opacity-50"
                  data-testid="upstream-capability-channel"
                >
                  {channelOptions.length === 0 ? (
                    <option value="">{t('upstream_detail.routing.capabilities.filter.channel.empty')}</option>
                  ) : (
                    channelOptions.map((channel) => (
                      <option key={channel} value={channel}>
                        {channel}
                      </option>
                    ))
                  )}
                </select>
              </label>
            </div>
          </div>
          {capabilities.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-surface-container-low" />
          ) : capabilities.isError ? (
            <p className="text-sm text-error">{coerceErrorMessage(capabilities.error)}</p>
          ) : capabilityGroups.length === 0 ? (
            <p className="text-sm text-outline">
              {t('upstream_detail.routing.capabilities.empty')}
            </p>
          ) : scopedCapabilityGroups.length === 0 ? (
            <p className="text-sm text-outline">
              {t('upstream_detail.routing.capabilities.empty_channel')}
            </p>
          ) : (
            <>
              <div
                className="mb-3 flex flex-wrap gap-2"
                data-testid="capabilities-summary"
              >
                {capabilityFilterItems.map((item) => {
                  const selected = resultFilter === item.filter;
                  const toneClass = item.tone === 'primary'
                    ? selected
                      ? 'bg-primary-container text-on-primary'
                      : 'bg-primary-container/20 text-primary'
                    : item.tone === 'error'
                      ? selected
                        ? 'bg-error text-on-error'
                        : 'bg-error/15 text-error'
                      : selected
                        ? 'bg-surface-container-highest text-on-surface'
                        : 'bg-surface-container-high text-on-surface-variant';
                  return (
                    <button
                      key={item.filter}
                      type="button"
                      onClick={() => setResultFilter(item.filter)}
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold transition hover:brightness-110 ${toneClass}`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
              {visibleCapabilityGroups.length === 0 ? (
                <p className="text-sm text-outline">
                  {t('upstream_detail.routing.capabilities.empty_filter')}
                </p>
              ) : (
                <div className="max-h-[420px] overflow-y-auto overflow-x-auto rounded-lg bg-surface-container-low">
                  <table className="w-full min-w-[760px] text-left text-xs" data-testid="capabilities-results-table">
                    <thead className="sticky top-0 bg-surface-container-high">
                      <tr>
                        <th className="w-[40%] px-3 py-2">
                          {t('upstream_detail.routing.capabilities.col.model')}
                        </th>
                        <th className="w-[44%] px-3 py-2">
                          {t('upstream_detail.routing.capabilities.col.status')}
                        </th>
                        <th className="w-[16%] px-3 py-2 text-right">
                          {t('upstream_detail.routing.capabilities.col.action')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCapabilityGroups.map((group) => {
                        const cap = group.representative;
                        const canBind = group.bindable !== null;
                        const isPolling = pollingCapabilityIdSet.has(cap.id) && cap.status === 'needs_probe';
                        const ignoreBusy = ignoreCapability.isPending || unignoreCapability.isPending;
                        return (
                          <tr key={group.key} className="border-t border-outline-variant/10">
                            <td className="px-3 py-3 align-top">
                              <div className="flex flex-col gap-1">
                                <span className="font-mono text-sm text-on-surface">{group.model}</span>
                                <span className="font-mono text-[11px] text-outline">{cap.vendor}</span>
                                {group.capabilities.length > 1 ? (
                                  <span className="text-[11px] text-outline">
                                    {t('upstream_detail.routing.capabilities.group.count', {
                                      count: group.capabilities.length,
                                    })}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex max-w-[18rem] flex-col gap-1">
                                <span
                                  className={`w-max rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] ${CAPABILITY_STATUS_CLASS[cap.status]}`}
                                >
                                  {capabilityStatusLabel(t, cap.status)}
                                </span>
                                <span className="line-clamp-2 text-[11px] leading-4 text-outline">
                                  {capabilityStatusHint(t, cap)}
                                </span>
                                {cap.status !== 'missing_from_list' && cap.status !== 'ignored' ? (
                                  <button
                                    type="button"
                                    disabled={probeCapability.isPending || isPolling}
                                    onClick={() => handleProbeCapability(cap)}
                                    className="mt-1 w-max text-[11px] font-semibold text-primary hover:underline disabled:text-outline disabled:no-underline"
                                  >
                                    {probingCapabilityId === cap.id
                                      ? t('upstream_detail.routing.capability.action.probing')
                                      : isPolling
                                        ? t('upstream_detail.routing.capability.action.waiting')
                                        : cap.status === 'passed'
                                          ? t('upstream_detail.routing.capability.action.reprobe')
                                          : t('upstream_detail.routing.capability.action.probe')}
                                  </button>
                                ) : null}
                                {cap.status === 'ignored' ? (
                                  <button
                                    type="button"
                                    disabled={ignoreBusy}
                                    onClick={() => handleUnignoreCapability(cap)}
                                    className="w-max text-[11px] font-semibold text-primary hover:underline disabled:text-outline disabled:no-underline"
                                  >
                                    {t('upstream_detail.routing.capability.action.unignore')}
                                  </button>
                                ) : group.bound === null ? (
                                  <button
                                    type="button"
                                    disabled={ignoreBusy}
                                    onClick={() => handleIgnoreCapability(cap)}
                                    className="w-max text-[11px] font-semibold text-outline hover:text-error hover:underline disabled:text-outline disabled:no-underline"
                                  >
                                    {t('upstream_detail.routing.capability.action.ignore')}
                                  </button>
                                ) : null}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {group.bound ? (
                                <span className="text-outline">
                                  {t('upstream_detail.routing.capability.action.bound')}
                                </span>
                              ) : cap.status === 'ignored' ? (
                                <span className="text-outline">
                                  {t('upstream_detail.routing.capability.action.ignored')}
                                </span>
                              ) : canBind ? (
                                <button
                                  type="button"
                                  disabled={createBinding.isPending}
                                  onClick={() => {
                                    if (group.bindable !== null) {
                                      handleBind(group.bindable);
                                    }
                                  }}
                                  className="text-primary hover:underline disabled:text-outline disabled:no-underline"
                                >
                                  {t('upstream_detail.routing.capability.action.bind')}
                                </button>
                              ) : (
                                <span className="text-outline">
                                  {t('upstream_detail.routing.capability.action.needs_pass')}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div>
          <div className="mb-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.08em] text-outline">
              {t('upstream_detail.routing.bindings.title')}
            </h4>
            <p className="mt-1 text-xs text-outline">
              {t('upstream_detail.routing.bindings.subtitle')}
            </p>
          </div>
          {bindings.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-surface-container-low" />
          ) : bindings.isError ? (
            <p className="text-sm text-error">{coerceErrorMessage(bindings.error)}</p>
          ) : visibleBindings.length === 0 ? (
            <p className="text-sm text-outline">
              {t('upstream_detail.routing.bindings.empty')}
            </p>
          ) : (
            <div className="max-h-[420px] overflow-y-auto overflow-x-auto rounded-lg bg-surface-container-low">
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead className="sticky top-0 bg-surface-container-high">
                  <tr>
                    <th className="px-3 py-2">
                      {t('upstream_detail.routing.bindings.col.channel')}
                    </th>
                    <th className="px-3 py-2">
                      {t('upstream_detail.routing.bindings.col.model')}
                    </th>
                    <th className="px-3 py-2">
                      {t('upstream_detail.routing.bindings.col.enabled')}
                    </th>
                    <th className="px-3 py-2 text-right">
                      {t('upstream_detail.routing.bindings.col.action')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleBindings.map((binding) => (
                    <tr key={binding.id} className="border-t border-outline-variant/10">
                      <td className="px-3 py-2">{binding.channel}</td>
                      <td className="px-3 py-2 font-mono">{binding.model}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            binding.enabled
                              ? 'font-semibold text-primary'
                              : 'font-medium text-outline'
                          }
                        >
                          {binding.enabled
                            ? t('upstream_detail.routing.bindings.enabled.yes')
                            : t('upstream_detail.routing.bindings.enabled.no')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => toggleBinding(binding)}
                          disabled={updateBinding.isPending}
                          className="mr-3 text-primary hover:underline disabled:text-outline"
                        >
                          {binding.enabled
                            ? t('upstream_detail.routing.bindings.action.disable')
                            : t('upstream_detail.routing.bindings.action.enable')}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeBinding(binding)}
                          disabled={deleteBinding.isPending}
                          className="text-error hover:underline disabled:text-outline"
                        >
                          {t('upstream_detail.routing.bindings.action.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ConfigurationCardProps {
  readonly upstream: UpstreamConfig;
  readonly attached: readonly UpstreamCatalogModel[];
}

function ConfigurationCard({ upstream, attached }: ConfigurationCardProps): JSX.Element {
  const t = useT();
  const update = useUpdateUpstream(upstream.id);
  const toast = useToast();
  const [mutationError, setMutationError] = useState<string | null>(null);

  async function handlePatch(body: Parameters<typeof update.mutateAsync>[0]): Promise<void> {
    setMutationError(null);
    try {
      await update.mutateAsync(body);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setMutationError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="upstream-config-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('upstream_detail.config.title')}
        </h3>
        {update.isPending ? (
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
            {t('common.saving')}
          </span>
        ) : null}
      </header>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <InlineTextField
          label={t('upstream_detail.config.display_name')}
          initial={upstream.name}
          testId="upstream-config-name"
          onSubmit={(value) => handlePatch({ name: value })}
        />
        <InlineTextField
          label={t('upstream_detail.config.endpoint_url')}
          initial={upstream.endpoint_url}
          testId="upstream-config-endpoint-url"
          mono
          onSubmit={(value) => handlePatch({ endpoint_url: value })}
        />
        <InlineNumberField
          label={t('upstream_detail.config.priority')}
          initial={upstream.priority}
          testId="upstream-config-priority"
          onSubmit={(value) => handlePatch({ priority: value })}
        />
        <SecretPreviewField upstream={upstream} />
        <CookiePassthroughField
          enabled={upstream.allow_cookie_passthrough}
          pending={update.isPending}
          onTogglePatch={async (nextEnabled) => {
            await handlePatch({ allow_cookie_passthrough: nextEnabled });
          }}
        />
        <MaintenanceToggleField
          upstream={upstream}
          attached={attached}
          pending={update.isPending}
          onTogglePatch={async (nextEnabled) => {
            await handlePatch({ enabled: nextEnabled });
          }}
        />
      </div>
      {mutationError !== null ? (
        <p
          className="text-xs font-medium text-error"
          role="alert"
          data-testid="upstream-config-error"
        >
          {mutationError}
        </p>
      ) : null}
    </article>
  );
}

function ChannelCostsCard({ upstreamId }: { readonly upstreamId: string }): JSX.Element {
  const t = useT();
  const costs = useUpstreamChannelCosts(upstreamId);
  const setCosts = useSetUpstreamChannelCosts(upstreamId);
  const toast = useToast();

  function handleSet(row: UpstreamChannelCostRecord, value: number): void {
    if (!Number.isFinite(value) || value <= 0) {
      toast.error(t('upstream_detail.channel_costs.invalid'));
      return;
    }
    setCosts.mutate(
      { costs: [{ channel_id: row.channel_id, cost_multiplier: value }] },
      {
        onSuccess: () => toast.success(t('upstream_detail.channel_costs.updated')),
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  return (
    <article className="flex flex-col gap-5 rounded-xl bg-surface-container p-8" data-testid="upstream-channel-costs-card">
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('upstream_detail.channel_costs.title')}
          </h3>
          <p className="mt-1 text-xs text-outline">
            {t('upstream_detail.channel_costs.subtitle')}
          </p>
        </div>
        {setCosts.isPending ? (
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
            {t('common.saving')}
          </span>
        ) : null}
      </header>

      {costs.isLoading ? (
        <div className="h-24 animate-pulse rounded-lg bg-surface-container-low" />
      ) : costs.isError ? (
        <p className="text-sm text-error">{coerceErrorMessage(costs.error)}</p>
      ) : (costs.data ?? []).length === 0 ? (
        <p className="text-sm text-outline">{t('upstream_detail.channel_costs.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-surface-container-low">
          <table className="w-full min-w-[680px] text-left text-xs">
            <thead className="bg-surface-container-high">
              <tr>
                <th className="px-4 py-3">{t('upstream_detail.channel_costs.col.channel')}</th>
                <th className="px-4 py-3">{t('upstream_detail.channel_costs.col.vendor')}</th>
                <th className="px-4 py-3">{t('upstream_detail.channel_costs.col.multiplier')}</th>
              </tr>
            </thead>
            <tbody>
              {(costs.data ?? []).map((row) => (
                <tr key={row.channel_id} className="border-t border-outline-variant/10">
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-semibold text-on-surface">{row.channel_slug}</span>
                      <span className="font-mono text-[11px] text-outline">{row.channel_id}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col" data-testid={`upstream-channel-cost-vendor-${row.channel_id}`}>
                      <span className="font-semibold text-on-surface">
                        {row.channel_vendor ?? '-'}
                      </span>
                      <span className="font-mono text-[11px] text-outline">
                        {row.channel_protocol ?? '-'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <InlineNumberField
                      label={t('upstream_detail.channel_costs.multiplier_label')}
                      initial={row.cost_multiplier}
                      testId={`upstream-channel-cost-${row.channel_id}`}
                      onSubmit={(value) => handleSet(row, value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

function SecretPreviewField({ upstream }: { readonly upstream: UpstreamConfig }): JSX.Element {
  const t = useT();
  return (
    <div
      className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-4"
      data-testid="upstream-config-secret-preview"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {t('upstream_detail.config.auth_secret')}
      </span>
      <span className="font-mono text-sm text-on-surface">
        {upstream.has_auth_secret
          ? upstream.auth_secret_preview ?? t('upstream_detail.config.auth_secret.write_only')
          : t('upstream_detail.config.auth_secret.empty')}
      </span>
      <span className="text-xs text-outline">
        {t('upstream_detail.config.auth_secret.hint')}
      </span>
    </div>
  );
}

interface CookiePassthroughFieldProps {
  readonly enabled: boolean;
  readonly pending: boolean;
  readonly onTogglePatch: (nextEnabled: boolean) => Promise<void>;
}

function CookiePassthroughField({
  enabled,
  pending,
  onTogglePatch,
}: CookiePassthroughFieldProps): JSX.Element {
  const t = useT();
  return (
    <label
      className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-4"
      data-testid="upstream-config-cookie-passthrough"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {t('upstream_detail.config.cookie_passthrough')}
      </span>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={enabled}
          disabled={pending}
          onChange={(e) => {
            void onTogglePatch(e.currentTarget.checked);
          }}
          data-testid="upstream-config-cookie-passthrough-toggle"
          className="mt-1 h-4 w-4 shrink-0 accent-primary disabled:opacity-40"
        />
        <div className="flex flex-col gap-1">
          <span className="text-sm text-on-surface">
            {enabled
              ? t('upstream_detail.config.cookie_passthrough.state.enabled')
              : t('upstream_detail.config.cookie_passthrough.state.disabled')}
          </span>
          <span className="text-xs text-outline">
            {t('upstream_detail.config.cookie_passthrough.hint')}
          </span>
        </div>
      </div>
    </label>
  );
}

interface InlineTextFieldProps {
  readonly label: string;
  readonly initial: string;
  readonly testId: string;
  readonly mono?: boolean;
  readonly onSubmit: (value: string) => Promise<void> | void;
}

function InlineTextField({ label, initial, testId, mono, onSubmit }: InlineTextFieldProps): JSX.Element {
  const [value, setValue] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setValue(initial);
    setDirty(false);
  }, [initial]);

  async function commit(): Promise<void> {
    if (!dirty) {
      return;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setValue(initial);
      setDirty(false);
      return;
    }
    await onSubmit(trimmed);
    setDirty(false);
  }

  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          setDirty(true);
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
        }}
        data-testid={testId}
        className={`w-full rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors${mono === true ? ' font-mono' : ''}`}
      />
    </label>
  );
}

interface InlineNumberFieldProps {
  readonly label: string;
  readonly initial: number;
  readonly testId: string;
  readonly onSubmit: (value: number) => Promise<void> | void;
}

function InlineNumberField({
  label,
  initial,
  testId,
  onSubmit,
}: InlineNumberFieldProps): JSX.Element {
  const [value, setValue] = useState<string>(String(initial));
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setValue(String(initial));
    setDirty(false);
  }, [initial]);

  async function commit(): Promise<void> {
    if (!dirty) {
      return;
    }
    const next = Number(value);
    if (!Number.isFinite(next)) {
      setValue(String(initial));
      setDirty(false);
      return;
    }
    await onSubmit(next);
    setDirty(false);
  }

  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <input
        type="number"
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          setDirty(true);
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
        }}
        data-testid={testId}
        className="w-full rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface font-mono border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors"
      />
    </label>
  );
}

interface ProbeCardProps {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly mappings: readonly UpstreamModelMappingRecord[];
}

interface HistoryEntry {
  readonly key: string;
  readonly result: ProbeResponse;
}

function ProbeCard({ upstreamId, upstreamName, mappings }: ProbeCardProps): JSX.Element {
  const t = useT();
  const probe = useProbeUpstream(upstreamId);
  const toast = useToast();
  const abortRef = useRef<AbortController | null>(null);
  const [vendor, setVendor] = useState('');
  const [channel, setChannel] = useState('');
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<readonly HistoryEntry[]>([]);

  const vendors = useMemo(() => [...new Set(mappings.map((m) => m.vendor))].sort(), [mappings]);
  const channels = useMemo(
    () => vendor ? [...new Set(mappings.filter((m) => m.vendor === vendor).map((m) => m.channel))].sort() : [],
    [mappings, vendor],
  );
  const models = useMemo(
    () => vendor && channel
      ? mappings
          .filter((m) => m.vendor === vendor && m.channel === channel)
          .map((m) => ({ key: `${m.vendor}:${m.channel}:${m.model}:${m.protocol}`, mapping: m }))
      : [],
    [mappings, vendor, channel],
  );

  useEffect(() => {
    return () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  async function handleProbe(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const match = models.find((o) => o.key === selected);
    if (match === undefined) {
      setError(t('upstream_detail.probe.error.select_model'));
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await probe.mutateAsync({
        body: {
          vendor: match.mapping.vendor,
          model: match.mapping.model,
          protocol: match.mapping.protocol as 'anthropic' | 'openai',
          channel: match.mapping.channel,
        },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setHistory((prev) => {
        const next: HistoryEntry = { key: `${res.probeId}-${Date.now()}`, result: res };
        return [next, ...prev].slice(0, MAX_PROBE_HISTORY);
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  return (
    <article
      data-testid="upstream-probe-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('upstream_detail.probe.title')}
        </h3>
        <span className="text-[10px] text-outline">{t('upstream_detail.probe.hint')}</span>
      </header>
      <form className="flex flex-col gap-3" onSubmit={handleProbe}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('upstream_detail.probe.vendor')}
            </span>
            <select
              value={vendor}
              onChange={(e) => { setVendor(e.currentTarget.value); setChannel(''); setSelected(''); }}
              disabled={probe.isPending || vendors.length === 0}
              data-testid="upstream-probe-vendor"
              className={INPUT_CLASS}
            >
              <option value="">{t('upstream_detail.probe.vendor_placeholder')}</option>
              {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('upstream_detail.probe.channel')}
            </span>
            <select
              value={channel}
              onChange={(e) => { setChannel(e.currentTarget.value); setSelected(''); }}
              disabled={probe.isPending || channels.length === 0}
              data-testid="upstream-probe-channel"
              className={INPUT_CLASS}
            >
              <option value="">{t('upstream_detail.probe.channel_placeholder')}</option>
              {channels.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('upstream_detail.probe.model')}
            </span>
            <select
              value={selected}
              onChange={(e) => setSelected(e.currentTarget.value)}
              disabled={probe.isPending || models.length === 0}
              data-testid="upstream-probe-select"
              className={INPUT_CLASS}
            >
              <option value="">{t('upstream_detail.probe.select.placeholder')}</option>
              {models.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.mapping.model} · {o.mapping.protocol}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={probe.isPending || selected.length === 0}
            data-testid="upstream-probe-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {probe.isPending
              ? t('upstream_detail.probe.submitting')
              : t('upstream_detail.probe.submit', { name: upstreamName })}
          </button>
        </div>
      </form>
      {error !== null ? (
        <p
          className="text-xs font-medium text-error"
          role="alert"
          data-testid="upstream-probe-error"
        >
          {error}
        </p>
      ) : null}
      {history.length > 0 ? (
        <div className="flex flex-col gap-4" data-testid="upstream-probe-history">
          {history.map((entry) => (
            <ProbeResultPanel
              key={entry.key}
              result={entry.result}
              testIdPrefix={`upstream-probe-result-${entry.key}`}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}

interface DangerZoneProps {
  readonly upstreamId: string;
  readonly upstreamName: string;
}

function DangerZone({ upstreamId, upstreamName }: DangerZoneProps): JSX.Element {
  const t = useT();
  const del = useDeleteUpstream(upstreamId);
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only fetch the impact snapshot while the dialog is open. Reset the
  // acknowledgement + error when the dialog re-opens so prior state
  // doesn't bleed through.
  const impactQ = useUpstreamRetirementImpact(upstreamId, { enabled: confirmOpen });

  async function handleConfirm(): Promise<void> {
    setError(null);
    try {
      await del.mutateAsync();
      setConfirmOpen(false);
      setAcknowledged(false);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
      // Force the operator to re-tick after a failed delete — the
      // error banner alone isn't enough; without this reset the
      // Delete button stays enabled and a second click fires a retry
      // without a fresh acknowledgement.
      setAcknowledged(false);
    }
  }

  function closeDialog(): void {
    if (del.isPending) return;
    setConfirmOpen(false);
    setAcknowledged(false);
    setError(null);
  }

  // Delete is gated by three conditions: (a) impact snapshot loaded
  // (we refuse to let the operator act without seeing the blast
  // radius); (b) operator ticked the acknowledgement box; (c) no
  // in-flight delete mutation already.
  const impactReady = impactQ.data !== undefined;
  const deleteEnabled = impactReady && acknowledged && !del.isPending;

  return (
    <article
      data-testid="upstream-danger-zone"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center gap-3">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-error">
          {t('upstream_detail.danger.title')}
        </h3>
      </header>
      <p className="text-sm text-outline">{t('upstream_detail.danger.body')}</p>
      <div>
        <button
          type="button"
          onClick={() => {
            setConfirmOpen(true);
            setAcknowledged(false);
            setError(null);
          }}
          data-testid="upstream-delete"
          className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
        >
          {t('upstream_detail.danger.delete')}
        </button>
      </div>
      {confirmOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="danger-confirm-title"
          aria-describedby="danger-confirm-description"
          data-testid="upstream-delete-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-surface-container-lowest/80 px-4"
        >
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-6 overflow-y-auto rounded-xl bg-surface-container-highest p-8 text-on-surface">
            <header className="flex flex-col gap-2">
              <h2 id="danger-confirm-title" className="text-base font-semibold">
                {t('upstream_detail.delete_modal.title', { name: upstreamName })}
              </h2>
              <p id="danger-confirm-description" className="text-sm text-outline">
                {t('upstream_detail.delete_modal.body')}
              </p>
            </header>

            {impactQ.isPending ? (
              <p
                className="text-xs text-outline"
                data-testid="upstream-retirement-loading"
                role="status"
              >
                {t('upstream_detail.retirement.loading')}
              </p>
            ) : null}

            {impactQ.isError ? (
              <div
                className="flex flex-col gap-2 rounded-lg bg-error-container p-4 text-xs text-on-error-container"
                role="alert"
                data-testid="upstream-retirement-error"
              >
                <span>{t('upstream_detail.retirement.load_error')}</span>
                <div>
                  <button
                    type="button"
                    onClick={() => void impactQ.refetch()}
                    className="inline-flex items-center justify-center rounded-lg bg-on-error-container px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-error-container transition-colors hover:bg-error"
                    data-testid="upstream-retirement-retry"
                  >
                    {t('upstream_detail.retirement.retry')}
                  </button>
                </div>
              </div>
            ) : null}

            {impactQ.data !== undefined ? (
              <RetirementImpactSummary impact={impactQ.data} />
            ) : null}

            {impactReady ? (
              <label
                className="flex items-start gap-3 rounded-lg bg-surface-container p-4 text-sm text-on-surface"
                data-testid="upstream-retirement-ack-label"
              >
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.currentTarget.checked)}
                  data-testid="upstream-retirement-ack"
                  className="mt-1 h-4 w-4 shrink-0 accent-primary"
                />
                <span>{t('upstream_detail.retirement.ack')}</span>
              </label>
            ) : null}

            {error !== null ? (
              <p
                className="text-xs text-error"
                role="alert"
                data-testid="upstream-delete-error"
              >
                {error}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeDialog}
                disabled={del.isPending}
                className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleConfirm()}
                disabled={!deleteEnabled}
                data-testid="upstream-delete-confirm"
                className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
              >
                {del.isPending ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}

/**
 * Three-card summary of the retirement-impact payload. Extracted so
 * the DangerZone render stays focused on dialog lifecycle.
 */
function RetirementImpactSummary({
  impact,
}: {
  readonly impact: {
    readonly uniquely_served_models: ReadonlyArray<{
      readonly vendor: string;
      readonly channel: string;
      readonly model: string;
      readonly protocol: string;
    }>;
    readonly total_attached_models: number;
    readonly traffic_share_24h: {
      readonly this_upstream_requests: number;
      readonly total_requests: number;
      readonly percentage: number;
    };
    readonly active_funding_batches: {
      readonly count: number;
      readonly remaining_usd_cents: number;
    };
  };
}): JSX.Element {
  const t = useT();
  const uniqueCount = impact.uniquely_served_models.length;
  return (
    <div
      className="flex flex-col gap-3"
      data-testid="upstream-retirement-summary"
    >
      <section
        className={[
          'flex flex-col gap-2 rounded-lg p-4 text-xs',
          uniqueCount > 0
            ? 'bg-error-container text-on-error-container'
            : 'bg-surface-container text-on-surface',
        ].join(' ')}
        data-testid="upstream-retirement-uniquely-served"
      >
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em]">
          {t('upstream_detail.retirement.uniquely_served.title')}
        </h3>
        {uniqueCount > 0 ? (
          <>
            <p>
              {t('upstream_detail.retirement.uniquely_served.body', {
                count: String(uniqueCount),
              })}
            </p>
            <ul className="list-disc space-y-1 pl-5 font-mono">
              {impact.uniquely_served_models.map((m) => (
                <li
                  key={`${m.vendor}/${m.channel}/${m.model}/${m.protocol}`}
                  data-testid="upstream-retirement-uniquely-served-row"
                >
                  {m.vendor}/{m.channel}/{m.model} · {m.protocol}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-outline">
              {t('upstream_detail.retirement.total_attached_suffix', {
                total: String(impact.total_attached_models),
                unique: String(uniqueCount),
              })}
            </p>
          </>
        ) : (
          <p>{t('upstream_detail.retirement.uniquely_served.none')}</p>
        )}
      </section>

      <section
        className="flex flex-col gap-2 rounded-lg bg-surface-container p-4 text-xs"
        data-testid="upstream-retirement-funding"
      >
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em]">
          {t('upstream_detail.retirement.funding.title')}
        </h3>
        {impact.active_funding_batches.count > 0 ? (
          <p>
            {t('upstream_detail.retirement.funding.body', {
              count: String(impact.active_funding_batches.count),
              remaining: formatUsdCents(
                impact.active_funding_batches.remaining_usd_cents,
              ),
            })}
          </p>
        ) : (
          <p>{t('upstream_detail.retirement.funding.none')}</p>
        )}
      </section>

      <section
        className="flex flex-col gap-2 rounded-lg bg-surface-container p-4 text-xs"
        data-testid="upstream-retirement-traffic"
      >
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em]">
          {t('upstream_detail.retirement.traffic.title')}
        </h3>
        {impact.traffic_share_24h.total_requests > 0 ? (
          <p>
            {t('upstream_detail.retirement.traffic.body', {
              percentage: impact.traffic_share_24h.percentage.toFixed(1),
              handled: String(impact.traffic_share_24h.this_upstream_requests),
              total: String(impact.traffic_share_24h.total_requests),
            })}
          </p>
        ) : (
          <p>{t('upstream_detail.retirement.traffic.no_traffic')}</p>
        )}
      </section>
    </div>
  );
}

// ─── Funding batches ────────────────────────────────────────────────────

interface FundingCardProps {
  readonly upstreamId: string;
  readonly accounts: readonly UpstreamAccountRecord[];
}

function formatCnyCents(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

function formatUsdCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatRate(rate: number): string {
  return rate.toFixed(4);
}

/**
 * "Funding batches" card — lists active + recent depleted batches
 * with a progress bar per batch + a "+ New batch" button that opens a
 * modal form.
 *
 * Rendered below the Probe card, above Danger Zone, on the upstream
 * detail page. Fetches via `useFundingBatches(upstreamId)`; the hook
 * is disabled when `upstreamId` is empty (no fetch while the parent
 * `useUpstream` is still loading).
 */
function FundingCard({ upstreamId, accounts }: FundingCardProps): JSX.Element {
  const t = useT();
  const batches = useFundingBatches(upstreamId);
  const commitMut = useCommitFundingBatch(upstreamId);
  const updateMut = useUpdateFundingBatch(upstreamId);
  const toast = useToast();
  const [newBatchOpen, setNewBatchOpen] = useState(false);

  const accountLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts) {
      m.set(a.id, a.label);
    }
    return m;
  }, [accounts]);

  const groupedBatches = useMemo(() => {
    const items = batches.data ?? [];
    const groups = new Map<string, UpstreamFundingBatchRecord[]>();
    for (const b of items) {
      const key = b.account_id ?? '__none__';
      const list = groups.get(key);
      if (list !== undefined) {
        list.push(b);
      } else {
        groups.set(key, [b]);
      }
    }
    return groups;
  }, [batches.data]);

  return (
    <article
      data-testid="upstream-funding-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('upstream_detail.funding.title')}
        </h3>
        <button
          type="button"
          onClick={() => setNewBatchOpen(true)}
          disabled={accounts.length === 0}
          data-testid="upstream-funding-new"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
        >
          {t('upstream_detail.funding.new')}
        </button>
      </header>

      {batches.isPending ? (
        <p className="text-sm text-outline" data-testid="upstream-funding-loading">
          {t('upstream_detail.funding.loading_aria')}
        </p>
      ) : null}
      {batches.isError ? (
        <p
          className="text-sm text-error"
          role="alert"
          data-testid="upstream-funding-error"
        >
          {batches.error instanceof OpsApiError
            ? `${batches.error.code}: ${batches.error.message}`
            : t('upstream_detail.funding.error.load')}
        </p>
      ) : null}
      {batches.data !== undefined && batches.data.length === 0 ? (
        <p className="text-sm text-outline" data-testid="upstream-funding-empty">
          {t('upstream_detail.funding.empty')}
        </p>
      ) : null}
      {batches.data !== undefined && batches.data.length > 0 ? (
        <div className="flex flex-col gap-6" data-testid="upstream-funding-list">
          {Array.from(groupedBatches.entries()).map(([accountKey, accountBatches]) => (
            <div key={accountKey} className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
                  {accountKey === '__none__'
                    ? t('upstream_detail.funding.account.unassigned')
                    : accountLabelMap.get(accountKey) ?? accountKey}
                </span>
              </div>
              <ul className="flex flex-col gap-3">
                {accountBatches.map((batch) => (
                  <FundingBatchRow
                    key={batch.id}
                    batch={batch}
                    onCommit={() => {
                      commitMut.mutate(batch.id, {
                        onSuccess: () => toast.success(t('upstream_detail.funding.toast.committed')),
                        onError: (err) => toast.error(coerceErrorMessage(err)),
                      });
                    }}
                    onUpdate={(body) => {
                      updateMut.mutate({ batchId: batch.id, body }, {
                        onSuccess: () => toast.success(t('upstream_detail.funding.toast.updated')),
                        onError: (err) => toast.error(coerceErrorMessage(err)),
                      });
                    }}
                    busy={commitMut.isPending || updateMut.isPending}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      {newBatchOpen ? (
        <NewFundingBatchModal
          upstreamId={upstreamId}
          accounts={accounts}
          onClose={() => setNewBatchOpen(false)}
        />
      ) : null}
    </article>
  );
}

interface FundingBatchRowProps {
  readonly batch: UpstreamFundingBatchRecord;
  readonly onCommit: () => void;
  readonly onUpdate: (body: Record<string, unknown>) => void;
  readonly busy: boolean;
}

function FundingBatchRow({ batch, onCommit, onUpdate, busy }: FundingBatchRowProps): JSX.Element {
  const t = useT();
  const pct = Math.max(0, Math.min(100, batch.pct_consumed));
  const barClass =
    pct >= 95
      ? 'bg-error'
      : pct >= 80
        ? 'bg-tertiary'
        : 'bg-primary';
  const [editing, setEditing] = useState(false);

  return (
    <li
      className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-4"
      data-testid={`upstream-funding-batch-${batch.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[11px] text-outline" data-testid={`upstream-funding-batch-id-${batch.id}`}>
            {batch.id}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-on-surface-variant">
            {t('upstream_detail.funding.batch.mode_rate', {
              mode: batch.mode,
              rate: formatRate(batch.effective_rate_cny_per_usd),
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {batch.committed ? (
            <StatusChip variant="ok" label={t('upstream_detail.funding.batch.chip.committed')} />
          ) : (
            <StatusChip variant="warning" label={t('upstream_detail.funding.batch.chip.draft')} />
          )}
          {batch.is_depleted ? (
            <StatusChip variant="error" label={t('upstream_detail.funding.batch.chip.depleted')} />
          ) : (
            <StatusChip variant="ok" label={t('upstream_detail.funding.batch.chip.active')} />
          )}
          {batch.alerted_95_at !== null ? (
            <StatusChip variant="error" label={t('upstream_detail.funding.batch.chip.alert_95')} />
          ) : batch.alerted_80_at !== null ? (
            <StatusChip variant="warning" label={t('upstream_detail.funding.batch.chip.alert_80')} />
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-[11px] text-on-surface-variant sm:grid-cols-4">
        <FundingMeta label={t('upstream_detail.funding.batch.meta.paid')} value={formatCnyCents(batch.cny_paid_cents)} />
        <FundingMeta label={t('upstream_detail.funding.batch.meta.capacity')} value={formatUsdCents(batch.usd_capacity_cents)} />
        <FundingMeta label={t('upstream_detail.funding.batch.meta.consumed')} value={formatUsdCents(batch.usd_consumed_cents)} />
        <FundingMeta label={t('upstream_detail.funding.batch.meta.funded_at')} value={batch.funded_at} mono={false} />
      </div>

      <div className="flex flex-col gap-1">
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-surface-container-high"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          data-testid={`upstream-funding-progress-${batch.id}`}
        >
          <div
            className={`h-full ${barClass}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-right text-[10px] text-outline">
          {t('upstream_detail.funding.batch.pct_consumed', { pct: String(pct) })}
        </span>
      </div>

      {batch.upstream_invoice_ref !== null || batch.note !== null ? (
        <div className="flex flex-col gap-1 border-t border-outline-variant pt-2 text-[11px] text-on-surface-variant">
          {batch.upstream_invoice_ref !== null ? (
            <span>
              <span className="font-bold uppercase tracking-[0.08em]">
                {t('upstream_detail.funding.batch.invoice')}
              </span>{' '}
              · {batch.upstream_invoice_ref}
            </span>
          ) : null}
          {batch.note !== null ? (
            <span>
              <span className="font-bold uppercase tracking-[0.08em]">
                {t('upstream_detail.funding.batch.note')}
              </span>{' '}
              · {batch.note}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Actions for uncommitted batches */}
      {!batch.committed ? (
        <div className="flex items-center justify-end gap-2 border-t border-outline-variant/10 pt-3">
          {editing ? (
            <BatchEditForm
              batch={batch}
              onSave={(body) => { onUpdate(body); setEditing(false); }}
              onCancel={() => setEditing(false)}
              busy={busy}
            />
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                disabled={busy}
                className="text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-40"
              >
                {t('upstream_detail.funding.batch.action.edit')}
              </button>
              <button
                type="button"
                onClick={onCommit}
                disabled={busy}
                className="inline-flex items-center justify-center rounded-lg bg-primary-container px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('upstream_detail.funding.batch.action.commit')}
              </button>
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}

function BatchEditForm({
  batch,
  onSave,
  onCancel,
  busy,
}: {
  readonly batch: UpstreamFundingBatchRecord;
  readonly onSave: (body: Record<string, unknown>) => void;
  readonly onCancel: () => void;
  readonly busy: boolean;
}): JSX.Element {
  const t = useT();
  const [cnyYuan, setCnyYuan] = useState(String(batch.cny_paid_cents / 100));
  const [invoiceRef, setInvoiceRef] = useState(batch.upstream_invoice_ref ?? '');
  const [note, setNote] = useState(batch.note ?? '');

  function handleSave(): void {
    const yuan = parseFloat(cnyYuan);
    const body: Record<string, unknown> = {};
    if (Number.isFinite(yuan) && yuan > 0) {
      const newCnyCents = Math.round(yuan * 100);
      if (newCnyCents !== batch.cny_paid_cents) {
        body.cny_paid_cents = newCnyCents;
        if (batch.upstream_deposit_rate !== null && batch.upstream_deposit_rate > 0) {
          body.usd_capacity_cents = Math.round(newCnyCents / batch.upstream_deposit_rate);
        }
      }
    }
    const trimRef = invoiceRef.trim();
    if (trimRef !== (batch.upstream_invoice_ref ?? '')) {
      body.upstream_invoice_ref = trimRef.length > 0 ? trimRef : null;
    }
    const trimNote = note.trim();
    if (trimNote !== (batch.note ?? '')) {
      body.note = trimNote.length > 0 ? trimNote : null;
    }
    if (Object.keys(body).length > 0) {
      onSave(body);
    } else {
      onCancel();
    }
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1">
          <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.cny_paid_yuan.label')}
          </span>
          <input
            type="number"
            step="0.01"
            value={cnyYuan}
            onChange={(e) => setCnyYuan(e.currentTarget.value)}
            disabled={busy}
            className="w-full rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface font-mono border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.invoice_ref.label')}
          </span>
          <input
            type="text"
            value={invoiceRef}
            onChange={(e) => setInvoiceRef(e.currentTarget.value)}
            disabled={busy}
            className="w-full rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.note.label')}
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            disabled={busy}
            className="w-full rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors"
          />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-xs font-medium text-outline hover:text-on-surface disabled:opacity-40"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={busy}
          className="text-xs font-bold text-primary hover:text-primary/80 disabled:opacity-40"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

interface FundingMetaProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function FundingMeta({ label, value, mono = true }: FundingMetaProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} text-on-surface`}>{value}</span>
    </div>
  );
}

interface NewFundingBatchModalProps {
  readonly upstreamId: string;
  readonly accounts: readonly UpstreamAccountRecord[];
  readonly onClose: () => void;
}

function NewFundingBatchModal({
  upstreamId,
  accounts,
  onClose,
}: NewFundingBatchModalProps): JSX.Element {
  const t = useT();
  const create = useCreateFundingBatch(upstreamId);
  const pricing = usePricing();
  const toast = useToast();
  const [accountId, setAccountId] = useState(accounts.length > 0 ? accounts[0]!.id : '');
  const [cnyPaidYuan, setCnyPaidYuan] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const rate = pricing.data?.policy?.cny_per_usd ?? null;

  const computedUsd = useMemo(() => {
    if (rate === null) return null;
    const yuan = parseFloat(cnyPaidYuan);
    if (!Number.isFinite(yuan) || yuan <= 0) return null;
    const cnyPaidCents = Math.round(yuan * 100);
    const usdCapacityCents = Math.round(cnyPaidCents / rate);
    return usdCapacityCents / 100;
  }, [rate, cnyPaidYuan]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);

    if (rate === null) {
      setFormError(t('upstream_detail.funding.error.rate_unavailable'));
      return;
    }

    if (accountId.length === 0) {
      setFormError(t('upstream_detail.funding.error.account_required'));
      return;
    }

    const yuan = parseFloat(cnyPaidYuan);
    if (!Number.isFinite(yuan) || yuan <= 0) {
      setFormError(t('upstream_detail.funding.error.cny_paid_required'));
      return;
    }

    const cnyPaidCents = Math.round(yuan * 100);
    const usdCapacityCents = Math.round(cnyPaidCents / rate);

    const body: CreateFundingBatchRequest = {
      mode: 'cny_to_usd',
      account_id: accountId,
      cny_paid_cents: cnyPaidCents,
      usd_capacity_cents: usdCapacityCents,
      upstream_deposit_rate: rate,
      ...(invoiceRef.trim().length > 0 ? { upstream_invoice_ref: invoiceRef.trim() } : {}),
      ...(note.trim().length > 0 ? { note: note.trim() } : {}),
    };

    try {
      const created = await create.mutateAsync(body);
      toast.success(t('upstream_detail.funding.toast.registered', { id: created.id }));
      onClose();
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setFormError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!create.isPending) onClose();
      }}
      titleId="funding-new-title"
      testId="upstream-funding-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="funding-new-title" className="text-base font-semibold text-on-surface">
          {t('upstream_detail.funding.register.title')}
        </h2>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.account.label')}
          </span>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.currentTarget.value)}
            disabled={create.isPending}
            data-testid="upstream-funding-account"
            className={INPUT_CLASS_ON_HIGHEST}
          >
            {accounts.length === 0 ? (
              <option value="">{t('upstream_detail.funding.form.account.none')}</option>
            ) : (
              accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))
            )}
          </select>
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.cny_paid_yuan.label')}
          </span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={cnyPaidYuan}
            onChange={(e) => setCnyPaidYuan(e.currentTarget.value)}
            data-testid="upstream-funding-cny-paid"
            disabled={create.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.exchange_rate.label')}
          </span>
          {pricing.isLoading ? (
            <span className="text-sm text-outline">{t('upstream_detail.funding.form.exchange_rate.loading')}</span>
          ) : rate !== null ? (
            <span className="font-mono text-sm text-primary" data-testid="upstream-funding-rate">{rate.toFixed(4)}</span>
          ) : (
            <span className="text-sm text-error">{t('upstream_detail.funding.error.rate_unavailable')}</span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.computed_usd.label')}
          </span>
          <span className="font-mono text-sm text-on-surface" data-testid="upstream-funding-usd-computed">
            {computedUsd !== null ? `$${computedUsd.toFixed(2)}` : '—'}
          </span>
        </div>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.invoice_ref.label')}
          </span>
          <input
            type="text"
            value={invoiceRef}
            onChange={(e) => setInvoiceRef(e.currentTarget.value)}
            data-testid="upstream-funding-invoice-ref"
            disabled={create.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_detail.funding.form.note.label')}
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            data-testid="upstream-funding-note"
            disabled={create.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>

        {formError !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="upstream-funding-error-form"
          >
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              if (!create.isPending) onClose();
            }}
            disabled={create.isPending}
            data-testid="upstream-funding-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={create.isPending || rate === null}
            data-testid="upstream-funding-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {create.isPending
              ? t('upstream_detail.funding.register.submitting')
              : t('upstream_detail.funding.register.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Maintenance toggle (Epic #109 / P2.11) ─────────────────────────────
//
// Replaces the raw enabled checkbox on the Configuration card with a
// guarded flow. Going INTO maintenance (currently enabled → disable)
// opens an impact-preview modal that lists the attached triples plus a
// type-to-confirm gate ("maintenance"). Coming OUT (disabled → enable)
// is low-risk and uses a lighter single-click confirm modal.
//
// The gateway selector already filters `enabled = 1` at the
// tenant-request routing sites (v1-messages candidate query, policy
// selector fallback), so flipping the column cuts off live traffic
// immediately. No schema change, no new endpoint.

interface MaintenanceToggleFieldProps {
  readonly upstream: UpstreamConfig;
  readonly attached: readonly UpstreamCatalogModel[];
  readonly pending: boolean;
  readonly onTogglePatch: (nextEnabled: boolean) => Promise<void>;
}

function MaintenanceToggleField({
  upstream,
  attached,
  pending,
  onTogglePatch,
}: MaintenanceToggleFieldProps): JSX.Element {
  const t = useT();
  const [draftOpen, setDraftOpen] = useState<false | 'enter' | 'resume'>(false);
  const [typed, setTyped] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);

  function openModal(direction: 'enter' | 'resume'): void {
    setDraftOpen(direction);
    setTyped('');
    setModalError(null);
  }

  function closeModal(): void {
    if (pending) return;
    setDraftOpen(false);
    setTyped('');
    setModalError(null);
  }

  async function handleConfirm(): Promise<void> {
    if (draftOpen === false) return;
    setModalError(null);
    if (draftOpen === 'enter') {
      // Type-to-confirm only when entering maintenance (the dangerous
      // direction). The literal matches the i18n key so operators who
      // read the modal body in either locale see the same token.
      const CONFIRM_TOKEN = 'maintenance';
      if (typed.trim() !== CONFIRM_TOKEN) {
        setModalError(t('upstream_detail.maintenance.confirm.type_mismatch'));
        return;
      }
    }
    try {
      await onTogglePatch(draftOpen === 'resume');
      setDraftOpen(false);
      setTyped('');
    } catch {
      // onTogglePatch surfaces errors via the caller's toast + mutation
      // error state; keep the modal open so the operator can retry.
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="upstream-config-maintenance">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {t('upstream_detail.maintenance.label')}
      </span>
      {upstream.enabled ? (
        <div className="flex items-center gap-3">
          <span className="text-sm text-on-surface" data-testid="upstream-config-enabled-state">
            {t('upstream_detail.maintenance.state.enabled')}
          </span>
          <button
            type="button"
            onClick={() => openModal('enter')}
            disabled={pending}
            data-testid="upstream-config-enter-maintenance"
            className="inline-flex items-center justify-center rounded-lg bg-tertiary-container px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-tertiary-container transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('upstream_detail.maintenance.enter.button')}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <span
            className="inline-flex w-fit items-center rounded-full bg-tertiary-container px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-tertiary-container"
            data-testid="upstream-config-maintenance-chip"
          >
            {t('upstream_detail.maintenance.state.disabled')}
          </span>
          <button
            type="button"
            onClick={() => openModal('resume')}
            disabled={pending}
            data-testid="upstream-config-resume-routing"
            className="inline-flex w-fit items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('upstream_detail.maintenance.resume.button')}
          </button>
        </div>
      )}

      {draftOpen !== false ? (
        <Modal
          isOpen
          onClose={closeModal}
          titleId="upstream-maintenance-modal-title"
          testId="upstream-maintenance-modal"
        >
          <div className="flex flex-col gap-6 p-8">
            <h2
              id="upstream-maintenance-modal-title"
              className="text-base font-semibold text-on-surface"
            >
              {draftOpen === 'enter'
                ? t('upstream_detail.maintenance.modal.title_enter', { name: upstream.name })
                : t('upstream_detail.maintenance.modal.title_resume', { name: upstream.name })}
            </h2>
            <p className="text-sm text-outline">
              {draftOpen === 'enter'
                ? t('upstream_detail.maintenance.modal.body_enter')
                : t('upstream_detail.maintenance.modal.body_resume')}
            </p>
            {draftOpen === 'enter' ? (
              <div
                className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-4"
                data-testid="upstream-maintenance-impact"
              >
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                  {t('upstream_detail.maintenance.impact.label', { count: attached.length })}
                </span>
                {attached.length === 0 ? (
                  <p className="text-[12px] text-outline">
                    {t('upstream_detail.maintenance.impact.none')}
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-2">
                    {attached.map((m) => (
                      <li
                        key={`${m.vendor}/${m.channel}/${m.model}/${m.protocol}`}
                        className="inline-flex items-center rounded-full bg-surface-container-highest px-3 py-1 font-mono text-[11px] text-on-surface"
                      >
                        {m.vendor}/{m.channel}/{m.model} ({m.protocol})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
            {draftOpen === 'enter' ? (
              <label className="flex flex-col gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                  {t('upstream_detail.maintenance.confirm.type_label')}
                </span>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.currentTarget.value)}
                  placeholder="maintenance"
                  disabled={pending}
                  autoComplete="off"
                  data-testid="upstream-maintenance-typed"
                  className={INPUT_CLASS_ON_HIGHEST}
                />
              </label>
            ) : null}
            {modalError !== null ? (
              <p
                role="alert"
                data-testid="upstream-maintenance-error"
                className="text-xs font-medium text-error"
              >
                {modalError}
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={pending}
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleConfirm()}
                disabled={
                  pending ||
                  (draftOpen === 'enter' && typed.trim() !== 'maintenance')
                }
                data-testid="upstream-maintenance-confirm"
                className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
              >
                {pending
                  ? t('common.saving')
                  : draftOpen === 'enter'
                  ? t('upstream_detail.maintenance.confirm.submit_enter')
                  : t('upstream_detail.maintenance.confirm.submit_resume')}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
